import { input } from '@aws-amplify/ui/lib/Input.css'
import { DirectiveFunction } from 'vue'
import { DirectiveBinding } from 'vue/types/options'
import { VNode } from 'vue/types/umd'
const isTextAreaComposing = (el: HTMLInputElement) => {
  const v = el.value
  return el.selectionStart !== v.length || v.substr(v.length - 2, 1) !== '\n'
}
const isTextArea = (el: HTMLInputElement) => {
  return el.tagName.toUpperCase() === 'TEXTAREA'
}
const isAutoComplete = (el: HTMLInputElement) => {
  return !!el.closest('.v-autocomplete')
}

const isAutoCompleteMenuOpen = (el: HTMLInputElement) => {
  return el
    .closest('.v-autocomplete')
    ?.classList.contains('v-select--is-menu-active') ?? false
}

// チップ形式の複数選択(ChipComboboxなど)かどうか。この場合プレーンなEnterは
// チェックボックスの選択には触れず、次のフィールドへ移動するだけにする
// (項目の選択はCtrl+Enterで行う)
const isMultiSelect = (el: HTMLInputElement) => {
  return !!el.closest('.v-select--is-multi')
}

// 日本語IMEを使用しているv-autocompleteの場合、次のフィールドへ移動する前に
// ハイライトされている項目を確定するための追加のEnterイベントが必要。
const dispatchEnterEvent = (el: HTMLElement) => {
  const enterEvent = new KeyboardEvent('keydown', {
    key: 'Enter',
    code: 'Enter',
    keyCode: 13,
    which: 13,
    bubbles: true,
    cancelable: true
  })
  el.dispatchEvent(enterEvent)
}

// Vuetifyはハイライトされた項目がある場合(auto-select-firstなど)のみ、Enterで
// v-autocomplete/v-selectのメニューを閉じる。それ以外はキーボードでフォーカスを
// 移動してもメニューが開いたまま残ってしまう。ハイライト状態に関わらず同期的に
// 閉じてくれるEscapeで強制的に閉じる。
// Vuetifyのキー操作ハンドラは入力要素自身にバインドされているため、バブリング
// させる必要はない。bubbles: trueにすると、既にメニューが閉じていて
// Vuetify側がstopPropagationしないケースで、ダイアログのEscape閉じるハンドラ
// などの祖先要素まで届いてしまう(例: モーダルが意図せず閉じる)。
export const dispatchEscapeEvent = (el: HTMLElement) => {
  const escEvent = new KeyboardEvent('keydown', {
    key: 'Escape',
    code: 'Escape',
    keyCode: 27,
    which: 27,
    bubbles: false,
    cancelable: true
  })
  el.dispatchEvent(escEvent)
}

const nextFocus: DirectiveFunction = (
  el: HTMLElement,
  binding: DirectiveBinding,
  vnode: VNode
) => {
  // stopオプションの処理(他の入力へのフォーカス移動を止める)
  const option = binding?.arg
  const value = binding?.value

  let group: any
  let isStop = false
  let isDisabled = false
  switch (option) {
    case 'stop':
      group = 'default'
      isStop = true
      break
    default:
      group = option || 'default'
      break
  }
  if (value && value === 'disabled') isDisabled = true
  if (isDisabled) return
  bindInputGroup(el, group)

  // Vuetifyのラジオボタンはripple要素(入力より描画範囲が広く、DOM順で入力より
  // 後に来るため上に重なる)やラベルがクリックを拾い、実際の<input>にブラウザの
  // フォーカスが移らないことがある(Vuetify側はラベルクリックで意図的に
  // フォーカスさせない実装になっている)。フォーカスが外れるとEnterでの
  // ネクストフォーカスが使えなくなるため、クリック後に明示的にフォーカスし直す。
  el.addEventListener('click', () => {
    const radioInput = el.querySelector('input[type="radio"]') as HTMLInputElement | null
    radioInput?.focus()
  })

  // Macではcompositionendがkeyupより先に発火するため、Enterのkeyupが来た時点で
  // ev.isComposingは既にfalseになっている。フォーカス移動を抑制するため手動で追跡する。
  let justFinishedComposing = false
  el.addEventListener('compositionend', () => {
    justFinishedComposing = true
  })

  // ダイアログのEnter決定などで「keydownは別要素・keyupだけがこの要素」に
  // 漏れて届くことがある。対応するkeydownを受けていないEnter keyupでは
  // フォーカス移動しない。
  let enterKeydownReceived = false
  el.addEventListener('keydown', (ev: KeyboardEvent) => {
    if (ev.key === 'Enter' && ev.isTrusted) enterKeydownReceived = true

    // Vuetifyのv-radioは素のinput自体へのclickをpreventDefaultし、選択処理は
    // 親の.v-radio要素のclickハンドラ(onChange)で行う実装になっている。
    // そのためキーボードのスペースキーによるネイティブのクリック動作だけでは
    // 選択が反映されないことがある。.v-radio自体をclick()して確実に選択する。
    if (ev.key === ' ') {
      const target = ev.target as HTMLInputElement
      if (target?.type === 'radio') {
        ev.preventDefault()
        ;(target.closest('.v-radio') as HTMLElement | null)?.click()
      }
    }
  })

  // Vuetify自身のEnterキー処理(ハイライトされている項目をトグル選択する)は
  // <input>自身にバインドされていて、bubbleフェーズのリスナーより先に
  // targetフェーズで動いてしまう。複数選択のドロップダウンでは、
  // Ctrl+Enterで選択した直後にプレーンなEnterを押すと、Vuetify自身が同じ項目を
  // もう一度トグルして解除してしまうため、captureフェーズで先回りして止める。
  // ここで止めるとbubbleフェーズの上のkeydownリスナーまで事前に止めてしまうため、
  // enterKeydownReceivedのセットはここで肩代わりする
  el.addEventListener(
    'keydown',
    (ev: KeyboardEvent) => {
      if (ev.key !== 'Enter' || ev.ctrlKey || !ev.isTrusted) return
      if (
        !isMultiSelect(el as HTMLInputElement) ||
        !isAutoCompleteMenuOpen(el as HTMLInputElement)
      )
        return
      enterKeydownReceived = true
      ev.stopPropagation()
      ev.preventDefault()
    },
    true
  )

  el.addEventListener('keyup', (ev: KeyboardEvent) => {
    if (ev.key === 'Enter' && (ev.isComposing || justFinishedComposing)) {
      justFinishedComposing = false
      return
    }
    justFinishedComposing = false
    if (ev.key === 'Enter') {
      const hadKeydown = enterKeydownReceived
      enterKeydownReceived = false
      if (!hadKeydown) return
      // 現在開いているダイアログ内の入力のみを対象にする
      const dialogs = document.querySelectorAll('.v-dialog.v-dialog--active')
      // 最後のダイアログを取得
      const dialog =
        dialogs.length > 0 ? dialogs[dialogs.length - 1] : undefined
      const doc = dialog ? dialog : document
      const inputs = Array.from(
        doc.querySelectorAll(
          `input:not([type="hidden"], [readonly], [disabled])[group="${group}"], textarea:not([type="hidden"], [readonly], [disabled], [type="checkbox"])`
        )
      ) as HTMLElement[]
      const targetElement = ev.target as HTMLElement
      const currentIndex = inputs.findIndex(
        (input) => input.id === targetElement.id
      )
      if (currentIndex < 0) return
      // textareaかどうかを確認
      if (
        isTextArea(inputs[currentIndex] as HTMLInputElement) &&
        isTextAreaComposing(inputs[currentIndex] as HTMLInputElement)
      )
        return
      if (isStop) return
      const nextIndex = currentIndex + 1

      // チェックボックス・ラジオボタンはclick()ではなくfocus()で移動する必要がある。
      // click()だとEnterでのフォーカス移動の副作用としてチェック/選択状態が
      // トグル・変更されてしまうため(ラジオボタンの選択はスペースキーで行う)。
      const focusOrClick = (target: HTMLElement) => {
        const type = (target as HTMLInputElement).type
        if (type === 'checkbox' || type === 'radio') {
          target.focus()
        } else {
          target.click()
        }
      }
      const moveToNext = () => {
        inputs[currentIndex].blur()
        if (nextIndex < inputs.length) {
          focusOrClick(inputs[nextIndex])
        } else {
          // 最後の入力の場合
          focusOrClick(inputs[0])
        }
      }

      // v-autocompleteの場合の処理
      // メニューが開いている場合のみ追加のEnterを発行する(日本語IMEのケースなど)。
      // メニューが既に閉じている場合(最初のEnterで選択済み)は、再度開いてしまう
      // のを避けるためスキップする。
      if (isAutoComplete(inputs[currentIndex] as HTMLInputElement)) {
        const wasMenuOpen = isAutoCompleteMenuOpen(inputs[currentIndex] as HTMLInputElement)
        // Ctrl+Enterは複数選択のドロップダウンで項目を選択するためのキーなので、
        // プレーンなEnterでの巡回対象にはしない。メニューも開いたままにして、
        // 続けて別の項目も選択できるようにする
        if (wasMenuOpen && ev.ctrlKey) return
        // チップ形式の複数選択では、プレーンなEnterはチェックボックスの選択には
        // 触れず、次のフィールドへ移動するだけにする(選択はCtrl+Enterで行う)
        if (
          wasMenuOpen &&
          !isMultiSelect(inputs[currentIndex] as HTMLInputElement)
        ) {
          dispatchEnterEvent(inputs[currentIndex])
        }
        // 上のEnterで何か選択されたかに関わらず強制的に閉じる。
        // フォーカス移動後にメニューが開いたまま残らないようにするため。
        dispatchEscapeEvent(inputs[currentIndex])
      }

      moveToNext()
    }
  })
}
// ここでは入力にgroupをタグ付けするだけにする。実際のdisabled/readonly状態は
// keyupハンドラ側でその都度チェックする。このタグ付けはマウント時(insertedフック)
// に一度しか実行されないため、ここで現在のdisabled/readonly状態に依存してしまうと、
// 最初はdisabledであとから有効になるフィールドがキーボード操作の対象から
// 永久に除外されてしまう。
function bindInputGroup(el: HTMLElement, group: string) {
  const inputs = Array.from(
    el.querySelectorAll(`input:not([type="hidden"])`)
  ) as HTMLElement[]
  inputs.forEach((i) => i.setAttribute('group', group))
}
export default nextFocus
